<template>
  <div>
    <Transition name="fade">
      <modal-devices v-if="app.state.matches('app.modal.visible.devices')" />
    </Transition>
    <Transition name="fade">
      <modal-permissions v-if="app.state.matches('app.modal.visible.permissions')" />
    </Transition>
    <Transition name="fade">
      <modal-qr v-if="app.state.matches('app.modal.visible.qr')" />
    </Transition>
    <Transition name="fade">
      <slow-network v-if="app.state.matches('app.modal.visible.slowNetwork')" />
    </Transition>
    <Transition name="fade">
      <modal-connections v-if="app.state.matches('app.modal.visible.connections')" />
    </Transition>
    <Transition name="fade">
      <a-loader v-if="app.isLoading" :message="app.loadingMessage" @cancel="loaderCancelHandler" />
    </Transition>
    <Transition name="fade">
      <connection-status v-if="app.state.matches('app.modal.visible.connectionStatus')" />
    </Transition>
    <Transition name="fade">
      <participants v-if="app.state.matches('app.modal.visible.participants')" />
    </Transition>
    <permissions-view v-if="app.state.matches('app.view.permissions')" />
    <join-view v-if="app.state.matches('app.view.join')" />
    <meeting-view v-if="app.state.matches('app.view.meeting')" />
    <exit-view v-if="app.state.matches('app.view.exit')" />
    <call-tab-view v-if="callTabNotice" :moved="callTab.state.value === 'moved'" @use-here="useHere" />
  </div>
</template>
<style lang="scss">
html,
body {
  overscroll-behavior: none;
  -webkit-overflow-behavior: none;
}
</style>
<script setup>
import { computed, watchEffect } from 'vue'
import { useAppStore, useSessionStore } from '@/stores'
import { useCallTab, useLanguage } from '@/composables'
import process from 'process'

import {
  BackendNotificationProcedure,
  BoardProcedure,
  ChatProcedure,
  ProfileProcedure,
  StreamProcedure,
  StartSessionProcedure,
  ParticipantDisjoinedProcedure,
  ParticipantDisconnectedProcedure,
  ParticipantJoinedProcedure,
  ParticipantMuteProcedure,
  PermissionsProcedure,
  ParticipantReadyProcedure,
  PeerErrorProcedure,
  RoleExchangeProcedure,
  TransformProcedure,
  TorchProcedure,
  ConnectionsProcedure,
  ScreensharingProcedure,
  RecordingProcedure,
  DestroyProcedure,
  DocumentEventProcedure,
  NotificationProcedure,
  SignalProcedure,
  MuteUnmuteProcedure,
  ChangeStrategyProcedure,
  ConnectionFailedProcedure,
  NetworkProcedure,
  LogProcedure,
  VideoHealthCheckProcedure,
  LiveCaptionProcedure,
  ViewportHeightProcedure,
} from './procedures'
import { jsonRpc, procedures } from '@communication'
import ConnectionStatus from '@/views/Modals/ConnectionStatus/ConnectionStatus.vue'
import Participants from '@/views/Modals/Participants/Participants.vue'
import { useTranslation } from 'i18next-vue'

const app = useAppStore()

window.process = process

watchEffect(() => {
  document.body.style.backgroundColor = app.state.matches('app.view.meeting') ? '#000' : '#FFF'
})
const { t, i18next } = useTranslation()
const setupProcedures = procedures => {
  procedures.add(new BackendNotificationProcedure())
  procedures.add(new BoardProcedure())
  procedures.add(new ChatProcedure())
  procedures.add(new ConnectionFailedProcedure())
  !!window.localStorage.getItem('airelink:logger') && procedures.add(new ConnectionsProcedure())
  procedures.add(new ParticipantDisjoinedProcedure())
  procedures.add(new StreamProcedure())
  procedures.add(new PermissionsProcedure())
  procedures.add(new ProfileProcedure())
  procedures.add(new ParticipantDisconnectedProcedure())
  procedures.add(new ParticipantJoinedProcedure())
  procedures.add(new ParticipantMuteProcedure())
  procedures.add(new ParticipantReadyProcedure())
  procedures.add(new PeerErrorProcedure())
  procedures.add(new RecordingProcedure())
  procedures.add(new RoleExchangeProcedure())
  procedures.add(new TransformProcedure())
  procedures.add(new TorchProcedure())
  procedures.add(new StartSessionProcedure())
  procedures.add(new ScreensharingProcedure())
  procedures.add(new DestroyProcedure())
  procedures.add(new DocumentEventProcedure())
  procedures.add(new NotificationProcedure())
  procedures.add(new SignalProcedure())
  procedures.add(new MuteUnmuteProcedure())
  procedures.add(new ChangeStrategyProcedure())
  procedures.add(new NetworkProcedure())
  procedures.add(new LogProcedure())
  procedures.add(new VideoHealthCheckProcedure())
  procedures.add(new LiveCaptionProcedure(t))
  procedures.add(new ViewportHeightProcedure())
}

const { language, initialize } = useLanguage()
const start = () => {
  app.transition('connected')
  app.transition('app')
}

// One call page per browser in the client app (useCallTab): another page shows a notice instead of asking for the
// camera, and "Use here" moves the call to it
const callTab = useCallTab()
const callTabNotice = computed(() => ['elsewhere', 'moved'].includes(callTab.state.value))
// Another page took the call over. In the call: leave it as closing the page would (disjoin, camera and microphone
// off) and close the signaling socket, so nothing reconnects or joins again; a reload would ask "Leave site?" here.
// Before the call: reload, which also drops a camera request that is still pending
const leaveForOtherPage = async () => {
  if (!app.state.matches('app.view.meeting')) return callTab.reloadAsMoved()
  // As ConnectionFailedProcedure: no "Leave site?" when this page is closed, no reaction to the others leaving
  useSessionStore().sessionFailed = true
  try {
    await procedures.get('destroyProcedure').silentlyDestroy()
  } catch (error) {
    console.error('[callTab] Leaving the call failed', error)
  }
  jsonRpc.close()
}
// The call is over on this page: another page may start one
watchEffect(() => {
  if (app.state.matches('app.view.exit')) callTab.end()
})
// The procedures are installed only on the page that has the call: a page that waits runs nothing that could ask for
// the camera (e.g. PermissionsProcedure when the permission changes). They are created now, during setup: several
// constructors call useTranslation(), which needs this component instance and throws after an await
const createdProcedures = []
setupProcedures({ add: procedure => createdProcedures.push(procedure) })
const begin = () => {
  createdProcedures.forEach(procedure => procedures.add(procedure))
  start()
}
const startWhenFree = async () => {
  if (await callTab.start(leaveForOtherPage)) begin()
}
const useHere = async () => {
  if (callTab.state.value === 'moved') return callTab.restart()
  if (await callTab.takeOver()) begin()
}

// The English texts are bundled (i18n.js): in English the app starts at once and shows the Locize texts when they
// arrive. Other languages wait for their texts (at most the timeout of useTranslations.js); without them the app
// starts in English.
const texts = initialize(language.value)
  .catch(error => console.error('Texts could not be loaded from Locize, using the bundled English ones', error))
if (i18next.hasResourceBundle(language.value, 'translation')) {
  startWhenFree()
} else {
  texts.finally(startWhenFree)
}

function loaderCancelHandler() {
  // TODO: is it safe?
  app.loaded()
}
</script>
